import loadable from "@loadable/component";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import React, { useRef } from "react";
import ReactModal from "react-modal";

export const Modal = loadable(
    () =>
        import(
            /* webpackChunkName: "react-modal" */
            "react-modal"
        )
);

interface LazyModalProps extends ReactModal.Props {
    children: React.ReactNode;
    noScroll?: boolean;
}

function LazyModal({
    isOpen,
    noScroll = true,
    children,
    ...props
}: LazyModalProps): React.ReactElement | null {
    const contentRef = useRef<HTMLDivElement>(null);

    if (!isOpen) {
        return null;
    }

    const deviceRegex = /iP(ad|hone)/;

    function onAfterOpen(): void {
        if (noScroll) {
            if (!deviceRegex.test(navigator.userAgent)) {
                disableBodyScroll(contentRef.current, {
                    // To disable scrolling on mobile, disableBodyScroll prevents touchmove events.
                    // However, there are cases where you have called disableBodyScroll on an element,
                    // but its children still require touchmove events to function.
                    // Such use cases are:
                    // 1. Having range input, textarea;
                    // 2. Scrollable content, inside of the LazyModal
                    // In order to allow touch events, you need to pass `data-allowTouch` to these elements.
                    allowTouchMove: (el) => {
                        while (el && el !== document.body) {
                            if (el.getAttribute("data-allowTouch") !== null) {
                                return true;
                            }

                            el = el.parentNode;
                        }
                    },
                });
            } else {
                const iOSBodyStyle = {
                    WebkitOverflowScrolling: "none",
                };
                Object.assign(document.body.style, iOSBodyStyle);
            }
        }
        props.onAfterOpen?.();
    }

    function onAfterClose(): void {
        if (noScroll) {
            if (!deviceRegex.test(navigator.userAgent)) {
                enableBodyScroll(contentRef.current);
            } else {
                const iOSBodyStyle = { WebkitOverflowScrolling: "initial" };
                Object.assign(document.body.style, iOSBodyStyle);
            }
        }
        props.onAfterClose?.();
    }

    return (
        <Modal {...props} isOpen={isOpen} onAfterOpen={onAfterOpen} onAfterClose={onAfterClose}>
            <div
                ref={contentRef}
                onTouchMove={(event) => {
                    // Avoid the propagation of the scrolling event to the body
                    event.stopPropagation();
                }}
            >
                {children}
            </div>
        </Modal>
    );
}

export default LazyModal;
